import type { Review } from '../../data'
import { authorInitials, guestsLabel, reviewMonthYear } from '../../helpers/utils'
import { useReviewsConfig } from '../../providers'

/**
 * Attributed-review header shared by every layout: avatar initials + reviewer
 * name, a date/guests subline, and a source badge on the right. Honors the
 * hideName / hideDate / hideSource toggles; renders nothing when every part
 * is hidden or absent.
 */
export function ReviewAttribution({ review, className }: { review: Review; className?: string }) {
  const options = useReviewsConfig()

  // API rows are only type-cast, never validated — sanitize author/channel to
  // strings up front so malformed data degrades instead of throwing.
  const author = typeof review.author === 'string' ? review.author.trim() : ''
  const name = author || 'Anonymous'
  const showName = !options.hideName
  const date = !options.hideDate ? reviewMonthYear(review.date) : ''
  const guests = guestsLabel(review.guests_count)
  const source =
    !options.hideSource && typeof review.channel === 'string' ? review.channel.trim() : ''

  const hasSubline = Boolean(date || guests)
  if (!showName && !hasSubline && !source) return null

  return (
    <div className={`homerunner-review-attribution flex items-center gap-3 min-w-0${className ? ` ${className}` : ''}`}>
      {showName && (
        <div className="homerunner-review-attribution-avatar" aria-hidden="true">
          {authorInitials(author)}
        </div>
      )}
      {(showName || hasSubline) && (
        <div className="homerunner-review-attribution-who flex flex-col min-w-0">
          {showName && (
            <span className="homerunner-review-attribution-author-name truncate text-foreground">
              {name}
            </span>
          )}
          {hasSubline && (
            <span className="homerunner-review-attribution-subline truncate text-muted-foreground">
              {date && <span className="homerunner-review-attribution-date">{date}</span>}
              {date && guests && (
                <span className="homerunner-review-attribution-sep" aria-hidden="true">
                  {' · '}
                </span>
              )}
              {guests && <span className="homerunner-review-attribution-guests">{guests}</span>}
            </span>
          )}
        </div>
      )}
      {source && <span className="homerunner-review-attribution-source">{source}</span>}
    </div>
  )
}
